import { otherJobsChoice } from './offer-agreement-choice'
import type { BookingOtherJobs } from '../functions/lib/customer-offer-data'
import { openBookingWelcome } from './booking-welcome'
// "Your first job" (Liam, 19 Oct 2026, after user testing: "It was not easy to book a job in and not simple. It needs to
// feel easy to book in the first customer. I think we need simplified and animations to guide through to that first
// booking"; drawn https://claude.ai/artifact/YSZ8fzFzGrBDiCgtsdi5u7, agreed with Astra over two rounds). One sheet, three
// steps: who it is for, when, and how much and how often. Each step slides in, a three-part lemon bar fills and says the
// answers so far (tap one to go back), and Next gives two gentle lemon pulses once the step is answered
// (docs/ANIMATION-STANDARD.md: nothing loops, nothing takes the keyboard or scrolls by itself, still with reduced motion).
// A new customer and their first visit are one save (POST /api/customers/book); someone already on the round is booked
// with Book a visit's own save. Then the Diary shows the visit on its day (schedule.ts 'roundly:first-visit').
import { api, getQueue, mutate, offQueueChange, onQueueChange, readCache } from './api'
import { openBooking } from './booking'
import { messageThemOffer } from './book-message'
import { bookWords } from './booking-days'
import { setTimesOn } from './business-settings'
import { wayFor } from './contact'
import { severalJobsOn } from './job-names'
import { firstName } from './late-message'
import { getMe } from './me'
import { takenOff } from './planning'
import { agreedTimeKey, comingUpDays } from './rebook-message'
import { currencySymbol, postcodeWords, shownTime } from './round-config'
import { keepAboveKeyboard } from './sheet-keyboard'
import type { Clean, Customer } from './types'
import { lineDrawing } from './picture-tiles'
import { timeWheel } from './time-wheel'
import { addDaysISO, clear, cycleLabel, cycleOptions, dayLabel, el, money, parseMoney, toast, todayISO } from './ui'
import { howOftenPicker, oftenKind, type OftenKind } from './how-often'
import { visitWords } from './visit-words'

export const FIRST_JOB_PATH = '/api/customers/book'

// A first job still on its way (no signal): never a second one started beside it. Someone already on the round is booked
// through their own visits' save, so that one is known by its key while this page is open.
let heldKey: string | null = null
// Still to send: waiting for signal, sending, or held until the owner signs in again or switches back to this round
// (Sol's re-check: neither is a refusal, so a second tap would book it twice).
const STILL_HELD = new Set(['queued', 'sending', 'signed-out', 'parked'])
export function firstJobWaiting(): boolean {
  return getQueue().some((q) => (q.path === FIRST_JOB_PATH || q.key === heldKey) && STILL_HELD.has(q.status))
}


interface Draft {
  otherJobs: BookingOtherJobs
  existing: Customer | null
  name: string; address: string; postcode: string; phone: string
  // Their exact spot, when pinned here (exact-spot.ts): saved as their Map link with them.
  mapsLink: string | null
  date: string; time: string
  dayAgreed: boolean | null; timeAgreed: boolean | null
  price: string
  // The first clean's price on a regular round (How often design D, 27 Oct 2026); the regular one is `price`.
  firstPrice: string
  // The tile picked (Regular can be picked before its number).
  kind: OftenKind | null
  cycle: { days: number | null; marker: string | null } | null
}

let open = false

// Book a visit's "+ New customer" (Liam, 19 Oct 2026: "Book a visit should give you the option of a new customer not just
// select a current one"): the same steps for someone new on a round that has customers, the name typed in the search
// already filled (in the name, address or postcode box, by what it looks like), and the day Book a visit was opened on
// already picked. Book a visit hands over the people it has just read, so the sheet opens at once.
export interface SomeoneNew { someoneNew: true; name?: string; address?: string; postcode?: string; date?: string; customers?: Customer[] }

export async function openFirstJob(opts?: SomeoneNew): Promise<void> {
  if (open) return
  if (firstJobWaiting()) { toast(opts ? 'Your last booking is still waiting for signal. It saves as soon as it can.' : 'Your first job is still waiting for signal. It saves as soon as it can.'); return }
  open = true
  // The people on the round, or the phone's saved copy of them with no signal. Neither: the sheet says so, so nobody
  // already on the round is added again as someone new.
  let customers: Customer[] | null = opts?.customers ?? null
  if (!customers) try { customers = (await api<{ customers: Customer[] }>('/api/customers')).customers } catch { customers = readCache<{ customers: Customer[] }>('customers')?.data.customers ?? null }
  try { draw(customers ?? [], customers === null, opts ?? null) } catch (err) { open = false; throw err }
}

function draw(customers: Customer[], unread: boolean, opts: SomeoneNew | null): void {
  const me = getMe()
  const timesOn = setTimesOn()
  const usual = me?.defaultCycleDays ?? null
  const usualOption = usual ? cycleOptions().find((o) => o.days === usual) : undefined
  const d: Draft = {
    otherJobs:null,
    existing: null, name: opts?.name?.trim() ?? '', address: opts?.address?.trim() ?? '', postcode: opts?.postcode?.trim() ?? '', phone: '', mapsLink: null,
    date: opts?.date && opts.date >= todayISO() ? opts.date : '', time: '', dayAgreed: null, timeAgreed: null, price: '', firstPrice: '',
    cycle: usual ? { days: usual, marker: usualOption?.marker ?? null } : null,
    kind: usual ? oftenKind({ days: usual, marker: usualOption?.marker ?? null }) : null,
  }
  // People on the round who could be this job: anyone not stopped or taken off, those with nothing booked first.
  const onRound = customers.filter((c) => !c.job_stopped_on && c.status !== 'lapsed' && !takenOff(c))
    .sort((a, b) => Number(!!a.next_booked) - Number(!!b.next_booked) || a.name.localeCompare(b.name))
  let step = 1
  let newPerson = onRound.length === 0 || !!opts
  // The sheet's words: the round's first job, or someone new from Book a visit.
  const heading = opts ? 'New customer' : 'Your first job'
  let busy = false
  // Held for signal: the sheet only says so; nothing in it moves until it lands.
  let held = false

  const dialog = el('dialog', { class: 'booking-dialog first-job-dialog', 'aria-labelledby': 'first-job-title' }) as HTMLDialogElement
  const close = el('button', { type: 'button', 'aria-label': `Close ${heading.toLowerCase()}` }, 'Close') as HTMLButtonElement
  const bar = el('div', { class: 'fj-bar', role: 'list', 'aria-label': 'Steps' })
  const body = el('div', { class: 'fj-body' })
  const error = el('p', { class: 'form-error', role: 'alert' })
  const back = el('button', { type: 'button', class: 'btn-secondary fj-back' }, 'Back') as HTMLButtonElement
  const next = el('button', { type: 'button', class: 'btn-primary fj-next' }, 'Next') as HTMLButtonElement
  const foot = el('footer', { class: 'booking-footer fj-foot' }, error, back, next)
  dialog.append(el('header', { class: 'booking-head' }, el('h2', { id: 'first-job-title' }, heading), close), bar, body, foot)
  const freeKeyboard = keepAboveKeyboard(dialog)
  const shut = (): void => { if (dialog.open && !busy) dialog.close() }
  close.addEventListener('click', shut)
  dialog.addEventListener('cancel', (e) => { if (busy) e.preventDefault() })
  dialog.addEventListener('close', () => { open = false; freeKeyboard(); dialog.remove() })
  document.body.append(dialog)
  dialog.showModal()

  const who = (): string => firstName(d.existing?.name ?? d.name) || d.existing?.name || d.name.trim() || 'them'
  const dayWords = (): string => d.date ? dayLabel(d.date) : ''
  const timeWords = (): string => (timesOn && d.time ? shownTime(d.time) : '')
  // Two gentle pulses on Next when its step is answered (the standard's guided hint), once each time it becomes ready.
  let wasReady = false
  const paintNext = (ready: boolean, words: string): void => {
    next.textContent = words
    next.disabled = !ready || busy
    if (ready && !wasReady) { next.classList.remove('fj-ready'); void next.offsetWidth; next.classList.add('fj-ready') }
    wasReady = ready
  }
  // How far the answers so far allow: a step is open only when every one before it is answered, so going back and
  // changing the person or the day closes what follows until it is answered again (Sonnet's and Sol's review).
  const whoDone = (): boolean => !!d.existing || (newPerson && !!d.name.trim())
  const whenDone = (): boolean => !!d.date && d.dayAgreed !== null && (!(d.dayAgreed && timeWords()) || d.timeAgreed !== null)
  const furthest = (): number => !whoDone() ? 1 : !whenDone() ? 2 : 3
  const paintBar = (): void => {
    clear(bar)
    const labels = [d.existing?.name ?? (d.name.trim() || 'Who'), d.date ? [dayWords(), timeWords()].filter(Boolean).join(', ') : 'When', 'Price']
    labels.forEach((text, i) => {
      const n = i + 1
      const seg = el('button', { type: 'button', role: 'listitem', class: `fj-seg${n <= step ? ' is-on' : ''}${n === step ? ' is-here' : ''}`, 'aria-current': n === step ? 'step' : 'false' },
        el('span', { class: 'fj-seg-line', 'aria-hidden': 'true' }), el('span', { class: 'fj-seg-words' }, `${n} ${text}`)) as HTMLButtonElement
      seg.disabled = n > furthest() || busy || held
      seg.addEventListener('click', () => go(n))
      bar.append(seg)
    })
  }
  const go = (n: number): void => {
    if (busy || held || n === step || n > furthest()) return
    const forward = n > step
    step = n
    wasReady = false
    render(forward ? 'fj-from-right' : 'fj-from-left')
  }
  back.addEventListener('click', () => { if (step > 1) go(step - 1) })

  const field = (label: string, input: HTMLElement, note?: string): HTMLElement =>
    el('label', { class: 'fj-field' }, el('span', {}, label, note ? el('small', {}, ` ${note}`) : ''), input)
  const choice = (label: string, on: boolean, act: () => void, cls = ''): HTMLButtonElement => {
    const b = el('button', { type: 'button', class: `fj-choice${cls ? ` ${cls}` : ''}`, 'aria-pressed': String(on) }, label) as HTMLButtonElement
    b.addEventListener('click', act)
    return b
  }

  function render(motion: string | null): void {
    clear(body)
    error.textContent = ''
    const panel = el('section', { class: `fj-step${motion ? ` ${motion}` : ''}` })
    body.append(panel)
    paintBar()
    back.hidden = step === 1
    if (step === 1) whoStep(panel)
    else if (step === 2) whenStep(panel)
    else priceStep(panel)
  }

  // ---- 1. Who's it for? ----
  function whoStep(panel: HTMLElement): void {
    panel.append(el('h3', {}, "Who's it for?"))
    if (!newPerson) {
      const search = el('input', { type: 'search', placeholder: postcodeWords('Name, address or postcode'), 'aria-label': 'Search your customers' }) as HTMLInputElement
      const list = el('div', { class: 'fj-people' })
      const drawList = (): void => {
        clear(list)
        const q = search.value.trim().toLowerCase().replace(/\s/g, '')
        const hits = (q ? onRound.filter((c) => [c.name, c.address, c.postcode].some((v) => (v ?? '').toLowerCase().replace(/\s/g, '').includes(q))) : onRound).slice(0, 20)
        for (const c of hits) {
          const row = el('button', { type: 'button', class: 'fj-person' }, el('strong', {}, c.name),
            el('span', {}, [c.address, c.reg_price_pence != null ? money(c.reg_price_pence) : 'no price yet', cycleLabel(c.cycle_days, c.cycle_text) ? `every ${cycleLabel(c.cycle_days, c.cycle_text)}` : ''].filter(Boolean).join(' · ')))
          row.addEventListener('click', () => {
            // Several jobs: which job the visit is for is asked on their own Book a visit sheet. So is someone already
            // booked: that sheet asks whether this changes their visit or adds another (one save here refuses a second).
            // From Book a visit's "+ New customer", their sheet keeps the day it was opened on and the ordinary agreement
            // switches (nobody has said it is arranged) (Sol's review).
            if (severalJobsOn() || c.next_booked) { dialog.close(); openBooking(opts ? { customerId: c.id, date: opts.date } : { customerId: c.id, arranged: true }); return }
            // Someone else: the day and time are asked about again for them.
            if (d.existing?.id !== c.id) { d.dayAgreed = null; d.timeAgreed = null }
            d.existing = c
            d.price = c.reg_price_pence != null ? (c.reg_price_pence / 100).toFixed(2) : ''
            go(2)
          })
          list.append(row)
        }
        if (!hits.length) list.append(el('p', { class: 'muted' }, 'Nobody on your round matches. Add them as someone new.'))
      }
      search.addEventListener('input', drawList)
      drawList()
      const fresh = el('button', { type: 'button', class: 'fj-new' }, '+ Someone new')
      fresh.addEventListener('click', () => {
        // From someone picked to someone new: their answers and price are not this person's.
        if (d.existing) { d.dayAgreed = null; d.timeAgreed = null; d.price = '' }
        newPerson = true; d.existing = null; render(null)
      })
      panel.append(search, el('p', { class: 'fj-caption' }, 'Nothing booked yet comes first'), list, fresh)
      paintNext(false, 'Pick who it is for')
      return
    }
    const box = (value: string, set: (v: string) => void, attrs: Record<string, string>): HTMLInputElement => {
      const i = el('input', { ...attrs }) as HTMLInputElement
      i.value = value
      i.addEventListener('input', () => { set(i.value); paintNext(whoDone(), 'Next: when'); paintBar() })
      return i
    }
    // A different first name is someone else (Sol's re-check): "Is Tuesday agreed with them?" is asked again.
    const name = box(d.name, (v) => {
      if (firstName(v) !== firstName(d.name)) { d.dayAgreed = null; d.timeAgreed = null }
      if(v!==d.name)d.otherJobs=null
      d.name = v
    }, { type: 'text', autocomplete: 'off', maxlength: '200', 'aria-label': 'Their name' })
    panel.append(
      field('Their name', name),
      field('Address', box(d.address, (v) => { d.address = v }, { type: 'text', autocomplete: 'off', maxlength: '500', 'aria-label': 'Address' })),
      field(postcodeWords('Postcode'), box(d.postcode, (v) => { d.postcode = v }, { type: 'text', autocomplete: 'off', maxlength: '20', 'aria-label': postcodeWords('Postcode') })),
      field('Mobile', box(d.phone, (v) => { d.phone = v }, { type: 'tel', autocomplete: 'off', maxlength: '100', 'aria-label': 'Mobile' }), 'optional, for texts and WhatsApp'),
    )
    // Exact spot, optional (27 Oct 2026, the pin's drawings): for a house Google Maps gets wrong. The same pin screens,
    // kept here until Book saves them.
    const spot = el('div', { class: 'fj-spot' }, el('strong', {}, 'Exact spot ', el('small', {}, 'optional')))
    const pinIt = (start?: 'here' | 'paste'): void => { void import('./exact-spot').then(({ openSpotSheet }) => openSpotSheet({ id: 0, name: d.name.trim() || 'them', link: d.mapsLink }, () => undefined, (link) => { d.mapsLink = link; render(null) }, start)) }
    if (d.mapsLink) {
      const off = el('button', { type: 'button', class: 'link-button' }, 'Take it off')
      off.addEventListener('click', () => { d.mapsLink = null; render(null) })
      const change = el('button', { type: 'button', class: 'link-button' }, 'Change')
      change.addEventListener('click', () => pinIt())
      spot.append(el('span', { class: 'spot-line is-pinned' }, lineDrawing('pin', 16), 'Exact spot pinned'), el('span', { class: 'fj-spot-links' }, change, off))
    } else {
      const way = (drawing: 'focus' | 'reply', words: string, start: 'here' | 'paste'): HTMLElement => {
        const b = el('button', { type: 'button', class: 'fj-spot-way' }, lineDrawing(drawing, 18), words)
        b.addEventListener('click', () => pinIt(start))
        return b
      }
      spot.append(el('div', { class: 'fj-spot-ways' }, way('focus', "I'm here now", 'here'), way('reply', 'Paste a link', 'paste')),
        el('small', { class: 'muted' }, 'For houses Google Maps gets wrong. You can add it later from the job.'))
    }
    panel.append(spot)
    if (onRound.length) {
      const pick = el('button', { type: 'button', class: 'link-button' }, 'Already on your round? Pick them')
      pick.addEventListener('click', () => { newPerson = false; render(null) })
      panel.append(pick)
    } else if (unread) {
      // No signal and no saved copy of the round: nobody can be picked, and the sheet says why.
      panel.append(el('p', { class: 'muted fj-note' }, "Your customers couldn't load. If they are on your round already, close this and try again with signal."))
    }
    paintNext(whoDone(), 'Next: when')
  }

  // ---- 2. When? ----
  function whenStep(panel: HTMLElement): void {
    panel.append(el('h3', {}, 'When are you going?'))
    const today = todayISO()
    const days = el('div', { class: 'fj-days' })
    for (let i = 0; i < 5; i++) {
      const iso = addDaysISO(today, i)
      const top = i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : dayLabel(iso).split(' ')[0]?.replace(',', '') ?? ''
      const b = el('button', { type: 'button', class: 'fj-day', 'aria-pressed': String(d.date === iso), 'aria-label': dayLabel(iso) },
        el('span', {}, top), el('strong', {}, dayLabel(iso).replace(/^[A-Za-z]+,?\s*/, ''))) as HTMLButtonElement
      b.addEventListener('click', () => pickDay(iso))
      days.append(b)
    }
    // Custom (Liam's iPhone, 26 Oct 2026: "it's not quite centre aligned. Maybe it should just say custom, because it also
    // puts a date in there, which doesn't make sense", and "it opened the Date picker, and then it closed it itself"): the
    // tile reads like the others, Custom over a calendar, or over the day picked; the phone's date box lies over it unseen.
    // An iPhone sets today in the box the moment its picker opens, and taking that at once redrew the step under the
    // open picker and shut it: on a touch screen the day is taken when the picker closes.
    const picked = !!d.date && d.date > addDaysISO(today, 4)
    const other = el('input', { type: 'date', min: today, class: 'fj-day-input', 'aria-label': picked ? `Custom day: ${dayLabel(d.date!)}` : 'Custom day' }) as HTMLInputElement
    if (picked) other.value = d.date!
    const touch = typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches
    let waiting: string | null = null
    const take = (): void => { const v = waiting; waiting = null; if (v && v >= today) pickDay(v) }
    other.addEventListener('change', () => { waiting = other.value; if (!touch || document.activeElement !== other) take() })
    other.addEventListener('blur', take)
    days.append(el('label', { class: 'fj-day fj-day-other', 'aria-pressed': String(picked) },
      el('span', {}, 'Custom'),
      picked ? el('strong', {}, dayLabel(d.date!)) : el('strong', { class: 'fj-day-cal', 'aria-hidden': 'true' }, lineDrawing('calendar', 20)),
      other))
    panel.append(days)
    function pickDay(iso: string): void {
      if (iso === d.date) return
      // A different day is a different arrangement: its answers are asked again.
      d.date = iso; d.dayAgreed = null; d.timeAgreed = null
      render(null)
    }
    // The agreement questions are redrawn on their own when the wheel turns, so the wheel is never rebuilt under a finger.
    const asks = el('div', { class: 'fj-asks' })
    if (timesOn) {
      const any = choice('Any time', !d.time, () => { d.time = ''; d.timeAgreed = null; render(null) })
      const set = choice('Set a time', !!d.time, () => { if (!d.time) { d.time = '12:00'; d.timeAgreed = null; render(null) } })
      panel.append(el('div', { class: 'fj-q' }, el('span', { class: 'fj-q-words' }, 'What time? ', el('small', {}, 'optional')), el('div', { class: 'fj-choices' }, any, set)))
      if (d.time) {
        // Roundly's own time wheel, in the form (Liam, 27 Oct 2026: the phone's wheel "dissapears like ive clicked away
        // also its not on brand"). It is the one from "When did you finish?"; the time box behind it stays for a
        // keyboard and a screen reader.
        const time = el('input', { type: 'time', class: 'time-chooser-input time-chooser-one', 'aria-label': 'Time' }) as HTMLInputElement
        time.value = d.time
        time.addEventListener('input', () => {
          if (!time.value || time.value === d.time) return
          d.time = time.value; d.timeAgreed = null
          drawAsks(); paintBar(); paintNext(whenDone(), 'Next: price')
        })
        panel.append(el('div', { class: 'fj-time time-chooser-wheeled' }, time, timeWheel(time).el))
      }
    }
    panel.append(asks)
    function drawAsks(): void {
      clear(asks)
      if (!d.date) return
      const dayQ = `Is ${dayLabel(d.date)} agreed with ${who()}?`
      asks.append(el('div', { class: 'fj-q' }, el('span', { class: 'fj-q-words' }, dayQ), el('div', { class: 'fj-choices fj-two' },
        choice('Yes', d.dayAgreed === true, () => { d.dayAgreed = true; render(null) }),
        choice('Not yet', d.dayAgreed === false, () => { d.dayAgreed = false; d.timeAgreed = null; render(null) }))))
      // The time is asked about only once the day is agreed: a time is never agreed for a day that is not.
      if (d.dayAgreed && timeWords()) {
        asks.append(el('div', { class: 'fj-q' }, el('span', { class: 'fj-q-words' }, `And ${timeWords()}?`), el('div', { class: 'fj-choices fj-two' },
          choice('Yes', d.timeAgreed === true, () => { d.timeAgreed = true; render(null) }),
          choice('Not yet', d.timeAgreed === false, () => { d.timeAgreed = false; render(null) }))))
      }
    }
    drawAsks()
    paintNext(whenDone(), 'Next: price')
  }

  // ---- 3. How much, and how often? ----
  function priceStep(panel: HTMLElement): void {
    const existing = d.existing
    // How often first, because it decides which prices to ask for (Liam, 27 Oct 2026, design D): a regular round asks for
    // the first clean and the price after it; Just once and Ad hoc one price (saved as both, so neither old figure can win
    // later: pricing.ts uses the first-clean price when there is no cycle or they come back late).
    panel.append(el('h3', {}, existing ? 'How much for this one?' : 'How often, and how much?'))
    const priceBox = (label: string, value: string, set: (v: string) => void, aria: string): HTMLElement => {
      const box = el('input', { inputmode: 'decimal', placeholder: 'Not priced yet', 'aria-label': aria }) as HTMLInputElement
      box.value = value
      box.addEventListener('input', () => { set(box.value); paintSummary() })
      return field(label, el('span', { class: 'fj-money' }, el('span', {}, currencySymbol()), box))
    }
    if (existing) {
      panel.append(priceBox('Price for this visit', d.price, (v) => { d.price = v }, 'Price for this visit'))
      panel.append(el('p', { class: 'muted fj-note' }, `${who()}'s usual price and how often stay as they are on their page.`))
    } else {
      const prices = el('div', { class: 'fj-prices' })
      const drawPrices = (): void => {
        clear(prices)
        const k = d.kind
        if (k === 'regular') {
          const weeks = d.cycle?.days ? cycleLabel(d.cycle.days, null) : ''
          prices.append(el('div', { class: 'fj-two-prices' },
            priceBox('First clean', d.firstPrice, (v) => { d.firstPrice = v }, 'First clean price'),
            priceBox(weeks ? `Then every ${weeks}` : 'Then each clean', d.price, (v) => { d.price = v }, 'Price for each clean after the first')),
            el('p', { class: 'muted fj-note' }, 'The first clean takes longer. Its price is also used if they come back after a long gap.'))
        } else if (k === 'once' || k === 'adhoc') {
          prices.append(priceBox(k === 'once' ? 'Price' : 'Price each time', d.price, (v) => { d.price = v }, k === 'once' ? 'Price' : 'Price each time'),
            el('p', { class: 'muted fj-note' }, k === 'once' ? "A one-off clean: nothing is booked after it, and they don't show as due." : 'No regular visits. They stay on your list, and the next booking starts on this price.'))
        } else prices.append(priceBox('Price for each clean', d.price, (v) => { d.price = v }, 'Price for each clean'), el('p', { class: 'muted fj-note' }, 'Pick how often to set a first clean price too.'))
      }
      const picker = howOftenPicker({ value: d.cycle, onChange: (v, k) => { d.kind = k; d.cycle = v; drawPrices(); paintSummary() } })
      panel.append(picker.node, prices)
      drawPrices()
    }
    const summary = el('p', { class: 'fj-summary' })
    const paintSummary = (): void => {
      let pence: number | null = null
      try { pence = parseMoney(d.price) } catch { pence = null }
      let first: number | null = null
      try { first = oftenKind(d.cycle) === 'regular' ? parseMoney(d.firstPrice) : null } catch { first = null }
      const agreed = d.dayAgreed ? (d.timeAgreed ? 'day and time agreed' : 'day agreed') : 'not agreed yet'
      const often = !existing && d.cycle ? (d.cycle.days ? `every ${cycleLabel(d.cycle.days, null)}` : d.cycle.marker ? cycleLabel(null, d.cycle.marker) : '') : ''
      const priceWords = first != null && pence != null && first !== pence ? `first clean ${money(first)}, then ${money(pence)}` : first != null ? money(first) : pence != null ? money(pence) : 'not priced yet'
      summary.textContent = [existing?.name ?? d.name.trim(), [dayWords(), timeWords()].filter(Boolean).join(', '), agreed, priceWords, often].filter(Boolean).join(' · ')
    }
    paintSummary()
    panel.append(summary)
    if(!existing)panel.append(otherJobsChoice(d.otherJobs,value=>{d.otherJobs=value}).node)
    paintNext(true, bookWords(d.date, timeWords(), false))
  }

  next.addEventListener('click', () => {
    if (next.disabled) return
    if (step < 3) { go(step + 1); return }
    // Every answer again before the save: an unanswered one opens its step instead.
    if (furthest() < 3) { go(furthest()); return }
    void save()
  })

  async function save(): Promise<void> {
    let pence: number | null
    try { pence = parseMoney(d.price) } catch (err) { error.textContent = (err as Error).message; return }
    // A regular round's first clean: today's visit is booked at it, the round carries on at the regular price.
    if (!d.existing && d.kind === 'regular' && !d.cycle) { error.textContent = 'Pick every how many weeks, or another kind of customer.'; return }
    let firstPence: number | null = null
    if (!d.existing && oftenKind(d.cycle) === 'regular') {
      try { firstPence = parseMoney(d.firstPrice) } catch (err) { error.textContent = (err as Error).message; return }
    }
    const single = !d.existing && (oftenKind(d.cycle) === 'once' || oftenKind(d.cycle) === 'adhoc')
    const confirmed = d.dayAgreed ? 1 : 0
    const slot = timesOn && d.time ? d.time : null
    const visit: Record<string, unknown> = {
      date: d.date, time_slot: slot, confirmed,
      time_agreed_for: confirmed && slot && d.timeAgreed ? agreedTimeKey(d.date, slot) : null,
      amount_pence: firstPence ?? pence,
    }
    busy = true; next.disabled = true; back.disabled = true; close.disabled = true; paintBar()
    next.textContent = 'Booking…'
    let queuedKey: string | null = null
    const existing = d.existing
    const ack = existing
      ? await mutate({ label: `Book ${existing.name} ${d.date}`, method: 'POST', path: `/api/customers/${existing.id}/cleans`, body: { ...visit, allow_additional: false }, onQueued: (m) => { queuedKey = m.key } })
      : await mutate({
        label: `${heading}: ${d.name.trim()} ${d.date}`, method: 'POST', path: FIRST_JOB_PATH,
        body: {
          customer: {
            name: d.name.trim(),
            ...(d.address.trim() ? { address: d.address.trim() } : {}),
            ...(d.postcode.trim() ? { postcode: d.postcode.trim() } : {}),
            ...(d.phone.trim() ? { phone: d.phone.trim() } : {}),
            ...(d.mapsLink ? { maps_link: d.mapsLink } : {}),
            ...(pence != null ? { reg_price_pence: pence } : {}),
            ...(firstPence != null ? { first_clean_price_pence: firstPence } : single && pence != null ? { first_clean_price_pence: pence } : {}),
            ...(d.cycle ? { cycle_days: d.cycle.days, cycle_text: d.cycle.marker } : {}),
          },
          visit, other_jobs:d.otherJobs,
        },
        onQueued: (m) => { queuedKey = m.key },
      })
    busy = false; close.disabled = false
    if (ack.ok) {
      const customer = (existing ?? ack.booked?.customer) as Customer | null | undefined
      const clean = (existing ? ack.row : ack.booked?.clean) as Clean | null | undefined
      dialog.close()
      // The landing is asked for first, so the Diary's next read is the one that shows it.
      if (customer && clean) landed(customer, clean, !opts)
      else toast(`${who()} is saved. Book the visit from their page.`)
      window.dispatchEvent(new CustomEvent('dropby:data-changed'))
      return
    }
    window.dispatchEvent(new CustomEvent('dropby:data-changed'))
    back.disabled = false; paintBar()
    if (ack.status >= 400 && ack.status < 500) {
      // Booked somewhere else since this list was read: plain words, not the save's own.
      // Only that refusal; any other (their job stopped meanwhile) keeps its own words.
      error.textContent = existing && ack.status === 409 && !ack.code && /already has a booking/i.test(ack.error ?? '')
        ? `${existing.name} already has a visit booked. Open their page to change it or add another.`
        : ack.error ?? 'Check the answers and try again.'
      paintNext(true, bookWords(d.date, timeWords(), false))
      return
    }
    // Held for signal: one change, sent as one; the sheet finishes when it lands, or the Diary shows it if closed.
    held = true; paintBar()
    error.textContent = `Waiting for signal. ${who()} is booked in as soon as it sends. You can close this.`
    next.hidden = true; back.hidden = true
    if (queuedKey) { if (existing) heldKey = queuedKey; watchLanding(queuedKey, existing) }
  }

  function watchLanding(key: string, existing: Customer | null): void {
    const finish = (): void => {
      held = false
      if (heldKey === key) heldKey = null
      window.removeEventListener('roundly:queued-landed', onLanded)
      offQueueChange(onRefused)
    }
    const onLanded = (e: Event): void => {
      const detail = (e as CustomEvent<{ key: string; row?: Record<string, unknown> }>).detail
      if (detail?.key !== key) return
      finish()
      const done = (customer: Customer | null, clean: Clean | null): void => {
        if (dialog.open) dialog.close()
        if (customer && clean) landed(customer, clean, !opts)
        else toast(`${who()} is booked in.`)
        window.dispatchEvent(new CustomEvent('dropby:data-changed'))
      }
      // Someone on the round: the answer is the visit itself.
      if (existing) { done(existing, (detail.row as unknown as Clean | undefined) ?? null); return }
      const cid = typeof detail.row?.id === 'number' ? detail.row.id : null
      if (cid === null) { done(null, null); return }
      // Someone new: the answer is the customer; their visit is read with them. If that read fails, it still landed.
      void api<{ customer: Customer; cleans: Clean[] }>(`/api/customers/${cid}`)
        .then((r) => done(r.customer, r.cleans.find((c) => c.state === 'booked' && c.date === d.date) ?? null))
        .catch(() => done(null, null))
    }
    const onRefused = (): void => {
      const entry = getQueue().find((q) => q.key === key)
      if (!entry || STILL_HELD.has(entry.status)) return
      finish()
      if (!dialog.open) return
      error.textContent = entry.error ?? `${who()} wasn't booked in. Check the saving indicator.`
      next.hidden = false; back.hidden = false; paintBar(); paintNext(true, bookWords(d.date, timeWords(), false))
    }
    window.addEventListener('roundly:queued-landed', onLanded)
    onQueueChange(onRefused)
  }

  render(null)
}

// Booked: the Diary on that day with the visit glowing, and a note saying what is booked; not agreed, the next step is
// Messages' own (book-message.ts messageThemOffer), or why there is nothing to send.
// The round's first job says so; someone new from Book a visit gets their own first visit.
function landed(customer: Customer, clean: Clean, firstEver: boolean): void {
  const who = firstName(customer.name) || customer.name
  const words = visitWords({ ...clean, state: 'booked' }, customer)
  const when = `${dayLabel(clean.date)}${clean.time_slot && setTimesOn() ? `, ${shownTime(clean.time_slot)}` : ''}`
  const agreedNow = clean.confirmed === 1
  const agreed = agreedNow ? [words.day, words.time === 'time agreed' ? words.time : ''].filter(Boolean).join(' · ') : 'not agreed yet'
  const offer = agreedNow ? null : messageThemOffer({ customer, conversation: { ...customer, last_clean: null }, jobId: null, date: clean.date, today: todayISO(), aheadDays: comingUpDays(), agreed: false, weekOnly: false, way: wayFor(customer), otherVisit: null })
  const action = customer.other_jobs_welcome ? {label:`Message ${who} the booking`,act:()=>openBookingWelcome(customer,clean)} : offer?.offer
    ? { label: `Message ${who} about ${dayLabel(clean.date)}`, act: () => void import('./screens/rebook').then((m) => m.openMessagesFor({ id: customer.id, name: customer.name }, 'customer', { jobId: null, visitId: clean.id, day: clean.date })) }
    : null
  const why = offer && !offer.offer && offer.why ? ` ${offer.why}` : ''
  window.dispatchEvent(new CustomEvent('roundly:first-visit', { detail: { id: clean.id, date: clean.date, title: `${who}'s first ${firstEver ? 'job' : 'visit'} is in the diary.`, tip: `${when} · ${agreed}.${why}`, action, another: true } }))
}
